import { useCallback, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Textarea from './components/Textarea';
import CargarArchivo from './components/CargarArchivo';
import { Boton, BotonLimpiar } from './components/Boton';
import { verificarCredenciales } from './auth';
import './App.css';

const API_BASE_PATH = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${process.env.PUBLIC_URL}/logo-railcom.svg`;

function App() {
  const textareaRef = useRef(null);
  const fileContentRef = useRef(null);
  const [autenticado, setAutenticado] = useState(false);
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [numerosCount, setNumerosCount] = useState(0);
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = (event) => {
    event.preventDefault();

    if (verificarCredenciales(usuario.trim(), password)) {
      setAutenticado(true);
      setLoginError('');
      setPassword('');
      return;
    }

    setLoginError('Usuario o contrasena incorrectos.');
  };

  const handleNumerosCountChange = useCallback((count) => {
    if (!fileContentRef.current) {
      setNumerosCount(count);
    }
  }, []);

  const handleFileLoaded = ({ text, fileName, count, previewInTextarea }) => {
    setError('');

    if (previewInTextarea) {
      fileContentRef.current = null;
      setFileInfo(null);
      textareaRef.current?.setText(text);
    } else {
      fileContentRef.current = { text, fileName };
      setFileInfo({ fileName, count });
      textareaRef.current?.clear();
      setNumerosCount(count);
    }
  };

  const handleLimpiar = () => {
    fileContentRef.current = null;
    setFileInfo(null);
    textareaRef.current?.clear();
    setNumerosCount(0);
    setError('');
  };

  const getContentForProcessing = () => {
    if (fileContentRef.current) {
      return {
        mode: 'file',
        text: fileContentRef.current.text,
        fileName: fileContentRef.current.fileName,
      };
    }

    return {
      mode: 'text',
      text: textareaRef.current?.getText() ?? '',
    };
  };

  const handleProcesar = async () => {
    const content = getContentForProcessing();
    const numeros =
      content.mode === 'file'
        ? content.text.trim()
        : textareaRef.current?.getNumeros() ?? [];

    const isEmpty =
      content.mode === 'file'
        ? !content.text.trim()
        : numeros.length === 0;

    if (isEmpty) {
      setError('Ingresá al menos un número telefónico o cargá un archivo .txt.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let response;

      if (content.mode === 'file') {
        const formData = new FormData();
        const blob = new Blob([content.text], { type: 'text/plain' });
        formData.append('archivo', blob, content.fileName);

        response = await fetch(`${API_BASE_PATH}/api/procesar-archivo`, {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await fetch(`${API_BASE_PATH}/api/procesar-numeros`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: content.text }),
        });
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Error al procesar los números telefónicos.');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || 'nollame_out.xlsx';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'No se pudo conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  if (!autenticado) {
    return (
      <div className="App">
        <header className="App-header">
          <form className="app-login" onSubmit={handleLogin}>
            <img
              src={LOGO_SRC}
              className="app-login__logo"
              alt="Railcom"
            />
            <h1>Registro no llame web App</h1>

            <TextField
              label="Usuario"
              value={usuario}
              onChange={(event) => setUsuario(event.target.value)}
              autoComplete="username"
              fullWidth
              required
              className="app-login__field"
            />
            <TextField
              label="Contrasena"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              fullWidth
              required
              className="app-login__field"
            />

            <Button type="submit" variant="contained" color="success" size="large" fullWidth>
              Ingresar
            </Button>

            {loginError && <p className="app-error">{loginError}</p>}
          </form>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Registro no llame web App</h1>
        <span className="app-subtitle">
          Ingresar los numeros uno abajo del otro, separados por salto de linea o espacios.
        </span>

        <div className="app-input-grid">
          <CargarArchivo
            onFileLoaded={handleFileLoaded}
            onError={setError}
            disabled={loading}
            fileInfo={fileInfo}
          />
          <Textarea ref={textareaRef} onNumerosCountChange={handleNumerosCountChange} />
        </div>

        <div className="app-botones">
          <Boton
            onProcesar={handleProcesar}
            loading={loading}
            disabled={numerosCount === 0 || loading}
          />
          <BotonLimpiar
            onProcesar={handleLimpiar}
            loading={loading}
            disabled={numerosCount === 0 || loading}
          />
        </div>

        {error && <p className="app-error">{error}</p>}
      </header>
    </div>
  );
}

export default App;
